import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Loader } from './Loader';
import { SvgIcon } from './SvgIcon';

/**
 * @typedef {object} Option
 * @property {string} name
 * @property {string|number} id
 */

/**
 * @callback onChangeCallback
 * @param {string|number} value
 */

/**
 * @param {object} props - Props.
 * @param {Option[]} props.options - Options.
 * @param {string|number} [props.selectedId] - Selected ID.
 * @param {string} props.label - Label text.
 * @param {string} props.id - A unique identifier.
 * @param {onChangeCallback} props.onChange - On change callback.
 * @param {object} [props.classNames] - Classes.
 * @param {string} [props.classNames.label] - Label classes.
 * @returns {JSX.Element} Component.
 */
export const Select = ( {
    options,
    selectedId,
    label,
    id,
    classNames,
    onChange
} ) => {
    const [ isOpen, setIsOpen ] = useState( false );
    const [ selectedOption, setSelectedOption ] = useState(
        getSelectedOption( options, selectedId )
    );
    
    useEffect( () => {
        setSelectedOption(
            getSelectedOption( options, selectedId )
        );
    }, [ options, selectedId ] );
    
    if ( !options || !selectedId || !selectedOption ) {
        return (
            <>
                <Label label={ label } id={ id } className={ classNames.label} />
                <div className="custom-select-v2">
                    <div className="custom-select-v2__loader-wrapper">
                        <Loader />
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Label label={ label } id={ id } className={ classNames.label } />

            <div
                className={ classnames( 'custom-select-v2', {
                    'custom-select-v2--open': isOpen
                } ) }
            >
                <button
                    aria-haspopup="listbox"
                    aria-labelledby={ id }
                    className="custom-select-v2__display"
                    onClick={ () => setIsOpen( val => !val ) }
                >
                    <div className="custom-select-v2__current">
                        { getSelectedOption( options, selectedId )?.name ?? '' }
                    </div>

                    <SvgIcon
                        className="custom-select-v2__chevron"
                        icon="filter-arrows"
                    />

                    <SvgIcon
                        className="custom-select-v2__close"
                        icon="close"
                    />

                </button>
                <div
                    className="custom-select-v2__options-wrapper"
                    { ...( isOpen ? {} : { 'aria-hidden': 'true' } ) }
                >
                    <ul role="listbox" className="custom-select-v2__options-list">
                        { options.map( option => {
                            return (
                                <li
                                    key={ option.id }
                                    className={ classnames( 'custom-select-v2__option', {
                                        'custom-select-v2__option--selected': option.id === selectedOption.id
                                    } ) }
                                >
                                    <button
                                        className="custom-select-v2__option-button"
                                        role="option"
                                        aria-selected={ option.id === selectedOption.id }
                                        onClick={ () => {
                                            setSelectedOption( option );
                                            onChange( option.id );
                                            setIsOpen( false );
                                        } }
                                    >
                                        { option.name }
                                    </button>
                                </li>
                            );
                        } ) }
                    </ul>
                </div>
            </div>
        </>
    );
};

Select.propTypes = {
    options: PropTypes.arrayOf(
        PropTypes.shape( {
            name: PropTypes.string.isRequired,
            id: PropTypes.oneOfType( [
                PropTypes.number,
                PropTypes.string
            ] ).isRequired
        } )
    ),
    selectedId: PropTypes.oneOfType( [
        PropTypes.number,
        PropTypes.string
    ] ),
    label: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    classNames: PropTypes.shape( {
        label: PropTypes.string
    } )
};

const getSelectedOption = ( options, selectedId ) => {
    if ( !options || selectedId === null ) { return null; }
    return options.find( option => option.id === selectedId );
};

// ===== Label ============================================================== //

const Label = ( { id, label, className = '' } ) => (
    <p className={ className } id={ id }>{ label }</p>
);

Label.propTypes = {
    label: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    className: PropTypes.string
};
